import { IPaymentInfo } from '../models/i-payment-models';
import { IDeliveryOrder, IInvoiceInfo, WebStorageService, StorageWorker } from '@cakeiteasy/web-utils';
import { Inject, Injectable } from '@angular/core';
import { IUser } from '../../users';
import { PAYMENT_PLATFORM_TYPES } from '../payment.constants';
import { Store } from '@ngrx/store';
import { CartState } from '../../cart/store/reducers/cart.reducer';
import { getCart } from '../../cart/store/selectors';
import { DELIVERY_ORDER_DELIVERY_TYPES } from '../../cart/constants/common';

export const KEEP_INFO_TYPES = {
  CONTACT_INFO: 'payment-info',
  INVOICE_INFO: 'invoice-info',
};


@Injectable()
export class PaymentStorageService {
  private STORAGE_PREFIX = 'cie-prev-payment-info';
  private APP_USAGE_PREFIX = '';
  // Recipient entered on the delivery step, used to fill empty contact fields on the payment step
  private _deliveryContact: Pick<IPaymentInfo, 'userName' | 'userPhone'> = null;

  constructor(private _storageManager: WebStorageService,
              @Inject('AppConfig') private _appConfig: any,
              private _store: Store<CartState>) {
    this.subscribe();
  }

  subscribe() {
    let bakeryId = null;
    this._store.select(getCart)
      .subscribe((cart) => {
        this._deliveryContact = this._getDeliveryContact(cart && cart.deliveryOrder);

        if (!cart || !cart.bakery) {
          return;
        }

        const clearOnBakeryChange = this._appConfig.paymentPrefilling &&
          this._appConfig.paymentPrefilling.clearStorageOnBakeryCartUpdate;

        if (clearOnBakeryChange && bakeryId && cart.bakery.id !== bakeryId) {
          this.clearStorage();
        }

        bakeryId = cart.bakery.id;
      });
  }

  setAppPrefix(prefix: string) {
    this.APP_USAGE_PREFIX = prefix;
  }

  updateContactInfo(platformType: string, modifiedInfo?: IPaymentInfo, user?: IUser, setCompanyData = false): void {
    const savedInfo = this._getSavedContactInfo(platformType);
    const userInfo = {
      userId: user && user.id || null,
      subscribedOnNews: user && user.subscribedOnNews || false,
      userEmail: user && user.email,
      userName: user && user.fullName || '',
      userPhone: user && user.phone,
    };
    const legalCompany = user && user.invoiceInfo && user.invoiceInfo.company && user.invoiceInfo.company.legalCompany;
    let companyData;
    if (setCompanyData && legalCompany) {
      companyData = {
        userCompanyName: legalCompany.name,
        invoiceCompanyId: user.invoiceInfo.company.id,
        invoiceCompanyOrgNumber: legalCompany.orgNumber,
        invoiceCompanyDiscount: user.invoiceInfo.company.discount,
        invoiceCompanyCountry: legalCompany.country
      };
    }
    modifiedInfo = { ...userInfo, ...companyData, ...savedInfo, ...modifiedInfo };
    this.storageWrapper.saveAsJson(this._getStorageKey(platformType, KEEP_INFO_TYPES.CONTACT_INFO), modifiedInfo);
  }

  getContactInfo(platformType: string): IPaymentInfo {
    const paymentInfo = this._getSavedContactInfo(platformType);
    if (!this._deliveryContact) {
      return paymentInfo;
    }

    const { userName, userPhone } = this._deliveryContact;
    return {
      ...paymentInfo,
      userName: paymentInfo?.userName || userName,
      userPhone: paymentInfo?.userPhone || userPhone,
    };
  }

  setInvoiceInfo(invoiceInfo: IInvoiceInfo): void {
    this.storageWrapper.saveAsJson(this._getStorageKey(PAYMENT_PLATFORM_TYPES.invoice, KEEP_INFO_TYPES.INVOICE_INFO), invoiceInfo);
  }

  getInvoiceInfo(): IInvoiceInfo {
    const invoiceInfo: IInvoiceInfo = this.storageWrapper
      .getAsJson(this._getStorageKey(PAYMENT_PLATFORM_TYPES.invoice, KEEP_INFO_TYPES.INVOICE_INFO));
    return Boolean(invoiceInfo) ? invoiceInfo : null;
  }

  clearStorage(ignoreKeys: string[] = []): void {
    const storage = this.storageWrapper.storage;
    const toRemove = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key.indexOf(this.STORAGE_PREFIX) === 0 &&
        !ignoreKeys.find((keyToIgnore) => key.indexOf(keyToIgnore) >= 0)) {
        toRemove.push(key);
      }
    }

    toRemove.forEach((key) => this.storageWrapper.remove(key));
  }

  removeInfo(platformType: string, infoType: string) {
    this.storageWrapper.remove(this._getStorageKey(platformType, infoType));
  }

  private _getSavedContactInfo(platformType: string): IPaymentInfo {
    const paymentInfo: IPaymentInfo = this.storageWrapper
      .getAsJson(this._getStorageKey(platformType, KEEP_INFO_TYPES.CONTACT_INFO));
    return Boolean(paymentInfo) ? paymentInfo : null;
  }

  private _getDeliveryContact(deliveryOrder: IDeliveryOrder): Pick<IPaymentInfo, 'userName' | 'userPhone'> {
    // Gift delivery holds the gift recipient's details, not the buyer's
    if (!deliveryOrder || deliveryOrder.deliveryType === DELIVERY_ORDER_DELIVERY_TYPES.GIFT) {
      return null;
    }

    const { deliveryUserName, deliveryPhone } = deliveryOrder;
    if (!deliveryUserName && !deliveryPhone) {
      return null;
    }

    return { userName: deliveryUserName || '', userPhone: deliveryPhone || undefined };
  }

  private get storageWrapper(): StorageWorker {
    return this._storageManager.getMostPersistentStorage();
  }

  private _getStorageKey(...keys): string {
    return [this.STORAGE_PREFIX, this.APP_USAGE_PREFIX, ...keys]
      .filter(Boolean)
      .map((k) => k.toLowerCase())
      .join('-');
  }
}
